import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';

/**
 * @typedef {object} SliderRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} modalContent
 * @property {HTMLElement} position
 * @property {HTMLTemplateElement[]} posts
 * @property {HTMLButtonElement} closeButton
 * @extends Component<SliderRefs>
 */
class ProductImageSlider extends Component {
  requiredRefs = ['dialog', 'modalContent', 'position', 'posts', 'closeButton'];

  /** @type {AbortController | undefined} */
  #controller;
  /** @type {MutationObserver | undefined} */
  #cloneObserver;
  /** @type {HTMLElement | undefined} */
  #opener;
  #index = 0;
  #ownsScrollLock = false;

  connectedCallback() {
    super.connectedCallback();
    this.#listen();
  }

  updatedCallback() {
    super.updatedCallback();
    this.close();
    this.#listen();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#cloneObserver?.disconnect();
    this.close();
    super.disconnectedCallback();
  }

  #listen() {
    this.#controller?.abort();
    this.#cloneObserver?.disconnect();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('click', this.#openPost, { signal });
    this.refs.dialog.addEventListener('click', this.#closeOnBackdrop, { signal });
    this.refs.dialog.addEventListener('close', this.#onClose, { signal });
    this.refs.dialog.addEventListener('cancel', this.#onCancel, { signal });
    this.addEventListener(CartAddEvent.eventName, this.#onCartAdd, { signal });
    this.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    this.addEventListener('shopify:block:deselect', this.close, { signal });

    const marquee = this.querySelector('marquee-component');
    if (marquee) {
      this.#cloneObserver = new MutationObserver(this.#hideClonesFromKeyboard);
      this.#cloneObserver.observe(marquee, { childList: true, subtree: true });
      this.#hideClonesFromKeyboard();
    }
  }

  #hideClonesFromKeyboard = () => {
    const groups = this.querySelectorAll('.rd-product-image-slider__items');
    for (const group of Array.from(groups).slice(1)) {
      group.setAttribute('aria-hidden', 'true');
      for (const link of group.querySelectorAll('a')) link.setAttribute('tabindex', '-1');
      for (const block of group.querySelectorAll('[data-shopify-editor-block]')) {
        block.removeAttribute('data-shopify-editor-block');
      }
    }
  };

  /** @param {MouseEvent} event */
  #openPost = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    if (!(event.target instanceof Element)) return;
    const card = event.target.closest('a[data-post-id]');
    if (!(card instanceof HTMLElement) || !this.contains(card)) return;
    const index = this.refs.posts.findIndex((post) => post.dataset.postId === card.dataset.postId);
    if (index < 0) return;
    event.preventDefault();
    this.#opener = card;
    this.#show(index);
  };

  /** @param {number} index */
  #show(index) {
    const { dialog } = this.refs;
    this.#loadPost(index);
    if (!dialog.open) {
      this.#ownsScrollLock = !document.documentElement.hasAttribute('scroll-lock');
      document.documentElement.setAttribute('scroll-lock', '');
      this.setAttribute('data-modal-open', '');
      dialog.showModal();
      this.refs.closeButton.focus({ preventScroll: true });
    }
  }

  /** @param {number} index */
  #loadPost(index) {
    const { posts, modalContent, position } = this.refs;
    const post = posts[index];
    if (!post) return;
    this.#stopVideo();
    this.#index = index;
    modalContent.replaceChildren(post.content.cloneNode(true));
    modalContent.scrollTop = 0;
    position.textContent = post.dataset.position ?? '';
    const video = modalContent.querySelector('video');
    if (video && !window.matchMedia('(prefers-reduced-motion: reduce)').matches && post.dataset.autoplay === 'true') {
      video.play().catch(() => {});
    }
  }

  #stopVideo() {
    for (const video of this.refs.modalContent.querySelectorAll('video')) video.pause();
  }

  /** Closes the shared dialog and releases its resources. */
  close = () => {
    if (this.refs.dialog.open) this.refs.dialog.close();
    this.#onClose();
  };

  #onClose = () => {
    if (this.refs.dialog.open) return;
    const wasOpen = this.hasAttribute('data-modal-open');
    this.#stopVideo();
    this.refs.modalContent.replaceChildren();
    this.removeAttribute('data-modal-open');
    if (this.#ownsScrollLock) {
      document.documentElement.removeAttribute('scroll-lock');
      this.#ownsScrollLock = false;
    }
    if (wasOpen && this.#opener?.isConnected) this.#opener.focus({ preventScroll: true });
  };

  /** @param {Event} event */
  #onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /** @param {MouseEvent} event */
  #closeOnBackdrop = (event) => {
    if (event.target === this.refs.dialog) this.close();
  };

  /** @param {CartAddEvent} event */
  #onCartAdd = (event) => {
    if (event.detail.data?.didError || !this.refs.dialog.open) return;
    this.close();
  };

  /** @param {CustomEvent<{blockId: string}>} event */
  #onBlockSelect = (event) => {
    const index = this.refs.posts.findIndex((post) => post.dataset.postId === event.detail.blockId);
    if (index >= 0) this.#show(index);
  };

  /** Shows the previous post, preserving focus on the navigation control. */
  previous() {
    this.#loadPost((this.#index - 1 + this.refs.posts.length) % this.refs.posts.length);
  }

  /** Shows the next post, preserving focus on the navigation control. */
  next() {
    this.#loadPost((this.#index + 1) % this.refs.posts.length);
  }
}

if (!customElements.get('rd-product-image-slider')) {
  customElements.define('rd-product-image-slider', ProductImageSlider);
}
